/* eslint-disable jsdoc/require-jsdoc */
import React, { useState } from 'react';
import SVGComponent from '../../../../../components/SVG/SVGComponent';
import { SVG_ICONS } from '../../../../../constants';
import {
  getDateForCalendar, getDateForFilter, getDateForIntervalFilter, getDateFromString, getYesterday,
} from '../../../../../utils/common';
import CalendarPopupCustomRange from './CalendarPopupCustomRange';
import validateFields from '../../../../../utils/validation';

const yesterday = getDateForCalendar(getYesterday());
const today = getDateForCalendar(new Date());

const PRESET_FILTER = {
  '30_MIN_AGO': '30#minutes ago',
  '5_HOURS_AGO': '5#hours ago',
  '24_HOURS_AGO': '24#hours ago',
  TODAY: `Today#${today}`,
  YESTERDAY: `Yesterday#${yesterday}`,
};

function CalendarPopup({ onChangeDate, onClose }) {
  const [selectedPresetFilter, setSelectedPresetFilter] = useState(PRESET_FILTER.TODAY);
  const [customDateRangeIsOpen, setCustomDateRangeIsOpen] = useState(false);
  const [customDateRangeErrorMsg, setCustomDateRangeErrorMsg] = useState(null);
  const [selectedDate, setSelectedDate] = useState({ from: new Date(), to: new Date() });
  const [selectedTime, setSelectedTime] = useState({ from: '00:00', to: '23:59' });

  function getIsActiveFilterClass(filterKey) {
    return !customDateRangeIsOpen && selectedPresetFilter === PRESET_FILTER[filterKey] ? 'active' : '';
  }

  function applyCustomDateRange() {
    const dateFrom = new Date(selectedDate.from);
    const dateTo = new Date(selectedDate.to);

    const [hourFrom, minuteFrom] = selectedTime.from.split(':');
    const [hourTo, minuteTo] = selectedTime.to.split(':');

    dateFrom.setHours(hourFrom);
    dateFrom.setMinutes(minuteFrom);

    dateTo.setHours(hourTo);
    dateTo.setMinutes(minuteTo);

    setCustomDateRangeErrorMsg(null);

    onChangeDate({
      from: getDateForIntervalFilter(dateFrom),
      to: getDateForIntervalFilter(dateTo),
    });
    onClose();
  }

  function validateAndApplyCustomDateRange(fromDate, fromTime, toDate, toTime) {
    const validationParams = [{
      label: 'To date',
      fieldName: 'toDate',
      value: `${getDateForFilter(new Date(toDate))} ${toTime}`,
      rules: ['mandatory', 'greaterThan'],
      comparatorValue: `${getDateForFilter(new Date(fromDate))} ${fromTime}`,
    }];

    validateFields(validationParams)
      .then(() => applyCustomDateRange()).catch((errorMgs) => {
        setCustomDateRangeErrorMsg(errorMgs);
      });
  }

  function apply() {
    if (customDateRangeIsOpen) {
      validateAndApplyCustomDateRange(
        selectedDate.from, selectedTime.from, selectedDate.to, selectedTime.to,
      );
      return;
    }

    const date = new Date();
    let from = '';
    let to = '';

    const [presetFilterValue, presetFilterLabel] = selectedPresetFilter.split('#');

    if (presetFilterLabel === 'minutes ago') {
      to = getDateForIntervalFilter(date);
      date.setMinutes(date.getMinutes() - parseInt(presetFilterValue));
      from = getDateForIntervalFilter(date);
    } else if (presetFilterLabel === 'hours ago') {
      to = getDateForIntervalFilter(date);
      date.setHours(date.getHours() - presetFilterValue);
      from = getDateForIntervalFilter(date);
    } else {
      const formattedDate = getDateFromString(presetFilterLabel);
      from = `${formattedDate} 00:00`;
      to = `${formattedDate} 23:59`;
    }

    onChangeDate({ from, to });
    onClose();
  }

  function closePopup() {
    onClose();
    setCustomDateRangeIsOpen(false);
    setCustomDateRangeErrorMsg(null);
  }

  function selectPresetFilter(filterValue) {
    if (!customDateRangeIsOpen) {
      setSelectedPresetFilter(filterValue);
    }
  }

  function toggleCustomDateRange() {
    setCustomDateRangeIsOpen((prev) => !prev);
    setCustomDateRangeErrorMsg(null);
  }

  function resetDate() {
    onChangeDate({ from: '', to: '' });
    setSelectedPresetFilter(PRESET_FILTER.TODAY);
    onClose();
    setCustomDateRangeIsOpen(false);
    setCustomDateRangeErrorMsg(null);
  }

  return (
    <div
      className="calendar__popup"
    >
      <div className="calendar__popup-heading-wrap">
        <div className="calendar__popup-heading">
          <h3>Select time range</h3>
          <SVGComponent
            className="icon-xxs calendar__popup-close"
            src={`${SVG_ICONS.utility}#close-sm`}
            onClick={closePopup}
          />
        </div>
      </div>

      <div className="d-flex">
        <div>
          <div className="calendar__popup-option">
            {
              Object.keys(PRESET_FILTER).map((key) => {
                const [value, label] = PRESET_FILTER[key].split('#');
                return (
                  <div
                    key={key}
                    className={`calendar__popup-row ${getIsActiveFilterClass(key)}`}
                    onClick={selectPresetFilter.bind(null, PRESET_FILTER[key])}
                  >
                    <div className="mr-4 w-30">{value}</div>
                    <span>{label}</span>
                  </div>
                );
              })
            }
          </div>
          <div
            className="calendar__popup-row mt-3 calendar-trigger"
            onClick={toggleCustomDateRange}
          >
            <span>Enter custom range</span>
          </div>
        </div>

        {customDateRangeIsOpen
          && (
            <CalendarPopupCustomRange
              onChangeDate={setSelectedDate}
              onChangeTime={setSelectedTime}
              errorMessage={customDateRangeErrorMsg}
            />
          )}
      </div>

      <div className="calendar__popup-button-wrap">
        <div className="calendar__popup-button">
          <button type="button" className="btn btn-secondary mr-auto" onClick={resetDate}>Reset</button>
          {' '}
          <button type="button" className="btn btn-secondary ml-4" onClick={closePopup}>Cancel</button>
          {' '}
          <button type="button" className="btn btn-primary ml-1" onClick={apply}>Apply</button>
        </div>
      </div>
    </div>
  );
}

export default CalendarPopup;
